import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

// Email template types
export type EmailTemplateType = 'userInvitation' | 'passwordReset' | 'notification';

// Email service interface
export interface EmailData {
  to: string;
  templateType: EmailTemplateType;
  templateData: Record<string, any>;
  customSubject?: string;
  customMessage?: string;
  fromName?: string;
  replyTo?: string;
  organizationId?: string;
  credentialId?: string;  // SMTP credential ID
}

export interface UserInvitationData {
  userEmail: string;
  userName: string;
  organizationName: string;
  userRole: string;
  organizationId: string;
  loginUrl?: string; // Optional, will be auto-filled with current origin
}

export interface NotificationData {
  to: string;
  subject: string;
  message: string;
  organizationName?: string;
  organizationId?: string;
  actionUrl?: string;
  actionText?: string;
  fromName?: string;
}

// Firebase Functions - FORCE PRODUCTION URLs
// console.log('📧 EmailService: Creating Firebase Functions callables');
// console.log('📧 Functions instance:', functions);
// console.log('📧 Functions app:', functions.app.options.projectId);

const sendEmailFunction = httpsCallable(functions, 'sendEmail');
const sendUserInvitationFunction = httpsCallable(functions, 'sendUserInvitation');
const sendNotificationFunction = httpsCallable(functions, 'sendNotification');
const sendTestEmailFunction = httpsCallable(functions, 'sendTestEmail');

// console.log('📧 Created functions:', {
//   sendEmail: sendEmailFunction,
//   sendUserInvitation: sendUserInvitationFunction,
//   sendNotification: sendNotificationFunction,
//   sendTestEmail: sendTestEmailFunction
// });

/**
 * Universal email sending service
 */
export const emailService = {
  /**
   * Send email with custom template
   */
  async sendEmail(data: EmailData): Promise<any> {
    try {
      const result = await sendEmailFunction(data);
      return result.data;
    } catch (error: any) {
      console.error('Error sending email:', error);
      throw new Error(error.message || 'Failed to send email');
    }
  },

  /**
   * Send user invitation email
   */
  async sendUserInvitation(data: UserInvitationData): Promise<any> {
    try {
      // Add current domain as loginUrl
      const currentOrigin = window.location.origin;
      const dataWithUrl = {
        ...data,
        loginUrl: currentOrigin
      };
      
      // console.log('📧 Sending user invitation with data:', dataWithUrl);
      // console.log('📧 Current origin:', currentOrigin);
      // console.log('📧 Using function:', sendUserInvitationFunction);
      // console.log('📧 Function URL should be: https://us-central1-youcrm-5231b.cloudfunctions.net/sendUserInvitation');
      
      const result = await sendUserInvitationFunction(dataWithUrl);
      // console.log('📧 User invitation sent successfully:', result);
      return result.data;
    } catch (error: any) {
      console.error('📧 Error sending user invitation:', error);
      console.error('📧 Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      throw new Error(error.message || 'Failed to send invitation');
    }
  },

  /**
   * Send notification email
   */
  async sendNotification(data: NotificationData): Promise<any> {
    try {
      const result = await sendNotificationFunction(data);
      return result.data;
    } catch (error: any) {
      console.error('Error sending notification:', error);
      throw new Error(error.message || 'Failed to send notification');
    }
  },

  /**
   * Send test email (development only)
   */
  async sendTestEmail(to: string, organizationId?: string, credentialId?: string): Promise<any> {
    try {
      const result = await sendTestEmailFunction({ to, organizationId, credentialId });
      return result.data;
    } catch (error: any) {
      console.error('Error sending test email:', error);
      throw new Error(error.message || 'Failed to send test email');
    }
  },

  /**
   * Send welcome email to new user
   */
  async sendWelcomeEmail(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Üdvözöljük a YouCRM rendszerben!',
      message: `Kedves ${userName}!<br><br>
        Üdvözöljük a <strong>${organizationName}</strong> YouCRM rendszerében!<br><br>
        Fiókja sikeresen aktiválva lett. Most már hozzáférhet a rendszer összes funkciójához, amelyre jogosultsága van.<br><br>
        Ha bármilyen kérdése van, keressen minket bizalommal.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send account activation email
   */
  async sendAccountActivation(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Fiók aktiválva',
      message: `Kedves ${userName}!<br><br>
        Fiókját sikeresen aktiváltuk a <strong>${organizationName}</strong> YouCRM rendszerében.<br><br>
        Most már teljes hozzáféréssel rendelkezik a rendszerhez.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send role changed notification
   */
  async sendRoleChangedNotification(
    userEmail: string, 
    userName: string, 
    organizationName: string, 
    oldRole: string, 
    newRole: string,
    organizationId?: string
  ): Promise<any> {
    const roleNames = {
      'admin': 'Adminisztrátor',
      'user': 'Felhasználó',
      'super_admin': 'Szuper Adminisztrátor'
    };

    return this.sendNotification({
      to: userEmail,
      subject: 'Jogosultságok módosítva',
      message: `Kedves ${userName}!<br><br>
        A <strong>${organizationName}</strong> rendszerében az Ön jogosultságai módosultak.<br><br>
        Korábbi szerepkör: <strong>${roleNames[oldRole as keyof typeof roleNames] || oldRole}</strong><br>
        Új szerepkör: <strong>${roleNames[newRole as keyof typeof roleNames] || newRole}</strong><br><br>
        A változások azonnal érvénybe léptek.`,
      organizationName,
      organizationId,
      actionUrl: window.location.origin,
      actionText: 'Bejelentkezés',
      fromName: organizationName
    });
  },

  /**
   * Send account deactivation email
   */
  async sendAccountDeactivation(userEmail: string, userName: string, organizationName: string, organizationId?: string): Promise<any> {
    return this.sendNotification({
      to: userEmail,
      subject: 'Fiók deaktiválva',
      message: `Kedves ${userName}!<br><br>
        Tájékoztatjuk, hogy fiókja deaktiválásra került a <strong>${organizationName}</strong> YouCRM rendszerében.<br><br>
        Ha úgy gondolja, hogy ez tévedés, kérjük vegye fel a kapcsolatot a rendszergazdával.`,
      organizationName,
      organizationId,
      fromName: organizationName
    });
  }
};

export default emailService; 